import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, AlertCircle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import ArtisanHeader from "@/components/ArtisanHeader";
import ArtisanFooter from "@/components/ArtisanFooter";

export default function PaymentCallback({ embedded = false, params = null, onDone = null }) {
  const { user, token } = useAuth();
  const [urlParams] = useSearchParams();
  const searchParams = params || urlParams;
  const routerNavigate = useNavigate();
  // In the Paystack slide-up, leaving the result closes the sheet first.
  const navigate = useCallback((to, opts) => { onDone?.(); routerNavigate(to, opts); }, [onDone, routerNavigate]);
  const [status, setStatus] = useState("verifying"); // verifying, success, failed, error
  const [message, setMessage] = useState("Verifying your payment...");
  const [paymentData, setPaymentData] = useState(null);
  const [paymentType, setPaymentType] = useState(null); // "promotion" or "subcategory"

  useEffect(() => {
    const verifyPayment = async () => {
      // Get reference from URL params (Paystack adds ?reference=xxx)
      const reference = searchParams.get("reference");
      const trxref = searchParams.get("trxref");
      const type = searchParams.get("type"); // "subcategory" for subcategory packages
      const paymentRef = reference || trxref;

      setPaymentType(type || "promotion");

      // Re-poll a verify endpoint while the server reports the activation is still being claimed.
      const verifyWithRetry = async (url, tries = 4) => {
        let res = await axios.get(url);
        for (let i = 1; i < tries && res.data?.status === "processing"; i++) {
          await new Promise((r) => setTimeout(r, 2500));
          res = await axios.get(url);
        }
        return res;
      };

      if (!paymentRef) {
        setStatus("error");
        setMessage("No payment reference found");
        return;
      }

      if (!token) {
        // Wait for auth to be ready
        setTimeout(() => {
          if (!token) {
            setStatus("error");
            setMessage("Please log in to verify your payment");
          }
        }, 2000);
        return;
      }

      try {
        // Handle rideshare service charge settlement
        if (type === "service_charge") {
          const response = await axios.get(
            `${API}/artisans/me/service-charges/verify?reference=${paymentRef}&token=${token}`
          );
          if (response.data?.status === "completed") {
            setStatus("success");
            const applied = Number(response.data.applied_to_owed || 0).toFixed(2);
            const added = Number(response.data.added_to_credit || 0).toFixed(2);
            setMessage(`GHS ${applied} applied to owed balance, GHS ${added} added as prepaid credit.`);
            setPaymentData({
              reference: paymentRef,
              amount: Number(response.data.amount_ghs || 0),
            });
            toast.success(`Service charge paid · GHS ${Number(response.data.owed || 0).toFixed(2)} left`);
            setTimeout(() => {
              navigate("/professional/shared-earnings", { replace: true });
            }, 3000);
            return;
          }
          setStatus("failed");
          setMessage(response.data?.status ? `Payment status: ${response.data.status}` : "Payment not successful");
          return;
        }

        // Handle rideshare promotion (flexible days × per-day rate)
        if (type === "rideshare_promotion") {
          const response = await verifyWithRetry(
            `${API}/rideshare/promotion/verify?reference=${paymentRef}&token=${token}`
          );
          if (response.data?.status === "completed") {
            setStatus("success");
            setMessage(`Promotion active for ${response.data.days} day(s). Riders further away will now see you.`);
            setPaymentData({
              reference: paymentRef,
              days: response.data.days,
              boost_radius_km: response.data.boost_radius_km,
              expires_at: response.data.expires_at,
            });
            toast.success(`Promotion on until ${new Date(response.data.expires_at).toLocaleDateString()}`);
            setTimeout(() => {
              navigate("/professional/rideshare/promotion", { replace: true });
            }, 3000);
            return;
          }
          setStatus("failed");
          setMessage(response.data?.message || "Payment not successful");
          return;
        }

        // Handle rideshare priority slot payment
        if (type === "rideshare_priority") {
          const response = await verifyWithRetry(
            `${API}/rideshare/priority/verify?reference=${paymentRef}&token=${token}`
          );
          if (response.data?.status === "completed") {
            setStatus("success");
            setMessage("Priority slot activated! You'll now appear on top of the driver list.");
            setPaymentData({
              reference: paymentRef,
              tier: response.data.tier,
              days: response.data.days,
              expires_at: response.data.expires_at,
            });
            toast.success(`Priority on until ${new Date(response.data.expires_at).toLocaleDateString()}`);
            setTimeout(() => {
              navigate("/professional/rideshare/priority", { replace: true });
            }, 3000);
            return;
          }
          setStatus("failed");
          setMessage(response.data?.message || "Payment not successful");
          return;
        }

        // Handle subcategory package payment
        if (type === "subcategory") {
          const response = await axios.get(
            `${API}/artisan/verify-subcategory-payment?reference=${paymentRef}&token=${token}`
          );

          if (response.data?.message?.includes("verified") || response.data?.message?.includes("activated")) {
            setStatus("success");
            setMessage("Payment successful! Your service slots have been activated.");
            setPaymentData({
              reference: paymentRef,
              slots_added: response.data.slots_added,
              total_slots: response.data.total_slots,
              duration_days: response.data.duration_days,
              expires_at: response.data.expires_at
            });
            
            toast.success(`+${response.data.slots_added} slots · ${response.data.duration_days} days`);
            
            // Redirect to profile page after 3 seconds
            setTimeout(() => {
              navigate("/professional/profile", { replace: true });
            }, 3000);
            return;
          }
        }

        // Handle promotion payment (existing logic)
        const response = await axios.get(
          `${API}/payments/paystack/verify/${paymentRef}?token=${token}`
        );

        if (response.data?.data?.status === "success") {
          setStatus("success");
          setMessage("Payment successful! Your promotion is now active.");
          setPaymentData(response.data.data);
          
          // Get stored promotion data from sessionStorage
          const pendingPromotion = sessionStorage.getItem("pendingPromotion");
          
          if (pendingPromotion) {
            try {
              const promoData = JSON.parse(pendingPromotion);
              
              if (promoData.is_upgrade && promoData.promotion_id) {
                // Handle promotion upgrade/edit
                const additionalAmount = (promoData.amount_to_pay || 0) + (promoData.credits_used || 0);
                
                await axios.put(
                  `${API}/artisan/promotions/${promoData.promotion_id}?token=${token}`,
                  {
                    radius_km: promoData.radius_km,
                    duration_days: promoData.duration_days,
                    all_day: promoData.all_day,
                    time_slots: promoData.all_day ? ["all_day"] : (promoData.time_slots || []),
                    additional_amount: additionalAmount,
                    use_credits: promoData.use_credits || false,
                    credits_used: promoData.credits_used || 0,
                    payment_method: "paystack",
                    transaction_id: paymentRef
                  }
                );
                
                sessionStorage.removeItem("pendingPromotion");
                toast.success(`Upgraded · ${promoData.radius_km} km · ${promoData.duration_days} days`);
              } else {
                // Handle new promotion creation
                const totalPromotionValue = promoData.total_amount || 0;
                
                const promoResponse = await axios.post(
                  `${API}/artisan/promotions?token=${token}`,
                  {
                    radius_km: promoData.radius_km,
                    duration_days: promoData.duration_days,
                    all_day: promoData.all_day,
                    time_slots: promoData.all_day ? ["all_day"] : (promoData.time_slots || []),
                    name: promoData.name || `${promoData.radius_km}km Promotion`,
                    use_credits: promoData.use_credits || false,
                    credits_used: promoData.credits_used || 0,
                    amount: totalPromotionValue,
                    payment_method: "paystack",
                    transaction_id: paymentRef
                  }
                );
                
                sessionStorage.removeItem("pendingPromotion");
                
                // Check if promotion already existed (created by inline popup)
                if (promoResponse.data?.already_exists) {
                  toast.info("Promotion already active from your payment!");
                } else {
                  toast.success(`Promotion on · ${promoData.duration_days} days`);
                }
              }
            } catch (promoError) {
              console.error("Error with promotion:", promoError);
              if (promoError.response?.data?.detail?.includes("already have an active")) {
                toast.info("You already have an active promotion.");
                sessionStorage.removeItem("pendingPromotion");
              } else {
                toast.error(promoError.response?.data?.detail || "Failed to update promotion. Please try again.");
              }
            }
          }
          
          // Redirect to promotions page after 3 seconds
          setTimeout(() => {
            navigate("/professional/promotions", { replace: true });
          }, 3000);
          
        } else if (response.data?.data?.status === "pending") {
          setStatus("verifying");
          setMessage("Payment is still being processed. Please wait...");
          
          // Poll again in 3 seconds
          setTimeout(verifyPayment, 3000);
          
        } else {
          setStatus("failed");
          setMessage(response.data?.data?.message || "Payment was not successful");
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        setStatus("error");
        setMessage(error.response?.data?.detail || "Failed to verify payment");
      }
    };

    verifyPayment();
  }, [searchParams, token, navigate]);

  const card = (
      <div className={embedded ? "w-full bg-white p-6 text-center" : "max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center"} data-testid="pro-payment-result">
        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {status === "verifying" && (
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
          )}
          {status === "success" && (
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          )}
          {status === "failed" && (
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
          )}
          {status === "error" && (
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertCircle className="h-12 w-12 text-yellow-600" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <h1 className={`text-2xl font-bold mb-2 ${
          status === "success" ? "text-green-600" :
          status === "failed" ? "text-red-600" :
          status === "error" ? "text-yellow-600" :
          "text-gray-800"
        }`}>
          {status === "verifying" && "Verifying Payment"}
          {status === "success" && "Payment Successful!"}
          {status === "failed" && "Payment Failed"}
          {status === "error" && "Something Went Wrong"}
        </h1>

        <p className="text-gray-600 mb-6">{message}</p>

        {/* Payment Details for Subcategory Package */}
        {paymentData && status === "success" && paymentType === "subcategory" && (
          <div className="bg-purple-50 rounded-lg p-4 mb-6 text-left border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Package className="text-purple-600" size={20} />
              <span className="font-semibold text-purple-600">Service Slots Activated</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Slots Added</span>
                <span className="font-semibold text-purple-600">+{paymentData.slots_added}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Slots</span>
                <span className="font-semibold">{paymentData.total_slots}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Duration</span>
                <span className="font-semibold">{paymentData.duration_days} days</span>
              </div>
              {paymentData.expires_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Valid Until</span>
                  <span className="font-semibold">{new Date(paymentData.expires_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Details for Promotion */}
        {paymentData && status === "success" && paymentType !== "subcategory" && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-gray-500 mb-1">Reference</p>
            <p className="font-mono text-sm mb-3">{paymentData.reference}</p>
            {paymentData.amount && (
              <>
                <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                <p className="font-semibold text-lg text-green-600">
                  GH₵ {paymentData.amount.toFixed(2)}
                </p>
              </>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {status === "success" && (
          <p className="text-sm text-gray-500 mb-4">
            Redirecting to {paymentType === "subcategory" ? "profile" : "promotions"} page...
          </p>
        )}

        {(status === "failed" || status === "error") && (
          <div className="space-y-3">
            <Button
              onClick={() => navigate(paymentType === "subcategory" ? "/professional/profile" : "/professional/promotions")}
              className="w-full bg-[#4F988E] text-white hover:bg-[#3d7a72]"
            >
              Back to {paymentType === "subcategory" ? "Profile" : "Promotions"}
            </Button>
            <Button
              variant="outline"
              onClick={() => (embedded ? onDone?.() : window.location.reload())}
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Yorm Branding */}
        <div className="mt-8 pt-6 border-t">
          <p className="text-xs text-gray-400">
            Powered by Yorm • Connecting skills to opportunities
          </p>
        </div>
      </div>
  );
  if (embedded) return card;
  return (
    <div className="min-h-screen bg-gray-50">
      <ArtisanHeader />
      <div className="flex items-center justify-center p-4 pt-20">{card}</div>
      <ArtisanFooter />
    </div>
  );
}
